import { FC, useContext, useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import FormError, { errorClass } from "components/form/formError";
import FloatingField from "components/form/FloatingField/FloatingField";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "auth/accountProvider";
import { useTranslation } from "react-i18next";
import { useValidation } from "components/form/useValidation";
import InfoAlert from "components/alert/InfoAlert";
import { CognitoUser } from "amazon-cognito-identity-js";

const ResetPassword: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useContext(AccountContext);
  const rules = useValidation();

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setError,
    formState: { errors },
  } = useForm();

  const newPassword = watch("newPassword");
  const repeatPassword = watch("repeatPassword");
  const passwordsDoNotMatch =
    newPassword && repeatPassword && newPassword !== repeatPassword;

  const [cognitoError, setCognitoError] = useState<string>("");
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as {
    resetUsername: string;
    forceReset?: boolean;
    user?: CognitoUser;
  };

  // Two ways in: the emailed link (code + userId in the query string) or the
  // manual /send-code path, which carries the username in router state.
  const linkCode = searchParams.get("code");
  const linkUserId = searchParams.get("userId");
  const isLinkFlow = Boolean(linkCode && linkUserId);
  const resetUsername = locationState?.resetUsername ?? "";

  useEffect(() => {
    if (!isLinkFlow && !resetUsername) {
      navigate("/login");
    }
  }, [isLinkFlow, resetUsername, navigate]);

  const setErrorMessage = (error: string) => {
    setCognitoError(t(error, t("Reset password unknown untranslated error")));
  };

  const handleResetPassword = (form: any) => {
    setCognitoError("");

    if (form.newPassword !== form.repeatPassword) {
      setError("repeatPassword", {
        type: "validate",
        message: t("Passwords do not match"),
      });
      setCognitoError(t("Passwords do not match"));
      return;
    }

    const code = isLinkFlow ? (linkCode ?? "") : form.securityCode;
    const username = isLinkFlow ? "" : resetUsername;
    const userId = isLinkFlow ? (linkUserId ?? "") : "";

    resetPassword(code, username, form.newPassword, userId)
      .then(() => {
        navigate("/login", { state: { isResetPassword: true } });
      })
      .catch((error: any) => {
        setErrorMessage(error);
      });
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleResetPassword)}>
      <InfoAlert
        text={t(
          isLinkFlow
            ? "reset password link explanation"
            : "reset password explanation"
        )}
        customClass="custom-alert"
      />
      {!isLinkFlow && (
        <div
          className={`form-group required ${errorClass(
            errors,
            "securityCode"
          )}`}
        >
          <FloatingField
            id="input-code"
            data-testid="input-code"
            type="text"
            label={t("code")}
            hasError={!!errors.securityCode}
            {...register("securityCode", rules.required)}
          />
          <ErrorMessage {...{ errors }} name="securityCode" as={FormError} />
        </div>
      )}

    <div
      className={`form-group required ${errorClass(errors, "newPassword")}`}
    >
        <FloatingField
          id="input-new-password"
          data-testid="input-new-password"
          type={isPasswordVisible ? "text" : "password"}
          label={t("new password")}
          aria-label={t("new password")}
          hasError={!!errors.newPassword}
          trailing={
            <button
              id="new-password-toggler"
              data-testid="new-password-toggler"
              type="button"
              className="password-toggler"
              aria-label={t("toggle password visibility")}
              onClick={() => {
                setIsPasswordVisible(!isPasswordVisible);
              }}
            />
          }
          {...register("newPassword", rules.required)}
        />
        <ErrorMessage {...{ errors }} name="newPassword" as={FormError} />
      </div>

      <div
        className={`form-group required ${errorClass(
          errors,
          "repeatPassword"
        )}`}
      >
        <FloatingField
          id="input-repeat-password"
          data-testid="input-repeat-password"
          type={isPasswordVisible ? "text" : "password"}
          label={t("repeat new password")}
          aria-label={t("repeat new password")}
          hasError={!!errors.repeatPassword}
          trailing={
            <button
              id="repeat-password-toggler"
              data-testid="repeat-password-toggler"
              type="button"
              className="password-toggler"
              aria-label={t("toggle password visibility")}
              onClick={() => {
                setIsPasswordVisible(!isPasswordVisible);
              }}
            />
          }
          {...register("repeatPassword", {
            ...rules.required,
            validate: (value) =>
              value === getValues("newPassword") ||
              `${t("Passwords do not match")}`,
          })}
        />
        <ErrorMessage {...{ errors }} name="repeatPassword" as={FormError} />
        {passwordsDoNotMatch && (
          <FormError>{t("Passwords do not match")}</FormError>
        )}
      </div>

      <div className="form-group">
        <button
          id="submit-reset-password"
          data-testid="submit-reset-password"
          type="submit"
          className="btn btn-primary btn-login"
        >
          {t("reset password")}
        </button>
        {cognitoError && <FormError>{cognitoError}</FormError>}
      </div>

      <div className="form-group">
        <div className="row">
          <div className="col-6">
            <Link
              to={isLinkFlow ? "/login" : "/send-code"}
              className="btn btn-link"
              id="back-button"
            >
              <span className="vismaicon vismaicon-move-left-circle" />
              {t("back")}
            </Link>
          </div>
          <div className="col-6 d-flex align-items-center justify-content-end">
            <Link to="/send-code" state={{ resetUsername }}>
              {t("resend code")}
            </Link>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ResetPassword;
